import { PasswordValidationResult } from '@/types/password-validation.types';
import { COMMON_PASSWORDS } from '@/constants/common-passwords';

export const PASSWORD_MIN_LENGTH = 12;
export const PASSWORD_MAX_LENGTH = 128;

/**
 * Validates password strength against security requirements
 * Enforces 12+ character minimum, complexity rules, and common password prevention
 * 
 * @param password - The password to validate
 * @returns PasswordValidationResult with detailed validation info
 */
export function validatePasswordStrength(password: string): PasswordValidationResult {
  const trimmedPassword = password.trim();
  
  // Initialize requirements check
  const requirements = {
    minLength: trimmedPassword.length >= PASSWORD_MIN_LENGTH && trimmedPassword.length <= PASSWORD_MAX_LENGTH,
    hasUppercase: /[A-Z]/.test(trimmedPassword),
    hasLowercase: /[a-z]/.test(trimmedPassword),
    hasNumber: /[0-9]/.test(trimmedPassword),
    // Common special characters from standard keyboard
    hasSpecialChar: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(trimmedPassword),
    notCommon: !isCommonPassword(trimmedPassword)
  };

  // Collect errors with specific, actionable messages
  const errors: string[] = [];
  
  if (!requirements.minLength) {
    if (trimmedPassword.length < PASSWORD_MIN_LENGTH) {
      errors.push(`Password must be at least ${PASSWORD_MIN_LENGTH} characters long (currently ${trimmedPassword.length})`);
    } else {
      errors.push(`Password must not exceed ${PASSWORD_MAX_LENGTH} characters`);
    }
  }
  
  if (!requirements.hasUppercase) {
    errors.push('Password must include at least one uppercase letter (A-Z)');
  }
  
  if (!requirements.hasLowercase) {
    errors.push('Password must include at least one lowercase letter (a-z)');
  }
  
  if (!requirements.hasNumber) {
    errors.push('Password must include at least one number (0-9)');
  }
  
  if (!requirements.hasSpecialChar) {
    errors.push('Password must include at least one special character (!@#$%^&*)');
  }
  
  if (!requirements.notCommon) {
    errors.push('This password is too common. Please choose a more unique password.');
  }

  // Determine overall strength for future password strength meter (REG-004)
  const metRequirements = Object.values(requirements).filter(Boolean).length;
  const strength = metRequirements === 6 ? 'strong' :
                   metRequirements >= 4 ? 'good' :
                   metRequirements >= 3 ? 'fair' : 'weak';

  return {
    isValid: errors.length === 0,
    errors,
    strength,
    requirements
  };
}

/**
 * Checks if password is in common passwords list or matches common patterns
 * Case-insensitive comparison to catch variations
 * 
 * @param password - The password to check
 * @returns true if password is common, false otherwise
 */
function isCommonPassword(password: string): boolean {
  const lowerPassword = password.toLowerCase();
  
  // Check exact match against common passwords list
  if (COMMON_PASSWORDS.some(common => common.toLowerCase() === lowerPassword)) {
    return true;
  }
  
  // Check for common sequential/repeated patterns
  const commonPatterns = [
    /^123+$/,           // 123, 1234, 12345, 123456, etc.
    /^password/i,       // password, Password123, password!, etc.
    /^qwerty/i,         // qwerty, qwerty123, QWERTY, etc.
    /^abc+/i,           // abc, abcd, abcdef, etc.
    /^letmein/i,        // letmein, LetMeIn123, etc.
    /^welcome/i,        // welcome, Welcome1, Welcome123, etc.
    /^admin/i,          // admin, Admin123, administrator, etc.
    /^\d+$/,            // All digits (123456789012)
  ];
  
  return commonPatterns.some(pattern => pattern.test(password));
}

/**
 * Gets user-friendly error message for password validation
 * Returns the first error or empty string if valid
 * 
 * @param validationResult - The validation result
 * @returns Primary error message or empty string if valid
 */
export function getPasswordErrorMessage(validationResult: PasswordValidationResult): string {
  if (validationResult.isValid) return '';
  return validationResult.errors[0] || 'Password does not meet security requirements';
}
